// hooks/useAuth.js
import { useState, useEffect, createContext, useContext } from 'react';
import { useRouter } from 'next/router';
import { 
  secureLog, 
  logLoginAttempt, 
  logResponse, 
  logUserData,
  maskEmail 
} from '../utils/secureLogger';
import { withAuthRetry, authCircuitBreaker } from '../utils/retryUtils';

const AuthContext = createContext({
  user: null,
  loading: true,
  isAuthenticated: false,
  isAdmin: false,
  login: () => Promise.resolve({ success: false, error: 'Not available' }),
  register: () => Promise.resolve({ success: false, error: 'Not available' }),
  logout: () => Promise.resolve(),
  updateUser: () => Promise.resolve({ success: false, error: 'Not available' }),
  changePassword: () => Promise.resolve({ success: false, error: 'Not available' }),
  checkAuth: () => Promise.resolve(),
});

// Simple throttle function
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const router = useRouter();

  const checkAuth = async () => {
    try {
      if (!process.env.NEXT_PUBLIC_API_URL) {
        const adminSession = getAdminSessionFromStorage();
        if (adminSession) setUser(adminSession.user);
        setLoading(false);
        return;
      }

      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers,
        mode: 'cors',
        cache: 'no-cache',
        signal: controller.signal
      }).catch(() => {
        clearTimeout(timeoutId);
        return { ok: false, status: 0 };
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Keep token fresh in localStorage
        if (data.token) localStorage.setItem('token', data.token);
      } else if (response.status === 401) {
        // Token invalid/expired — clear everything and force re-login
        setUser(null);
        clearAdminSessionFromStorage();
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      } else if (response.status === 0) {
        // Network error — fall back to cached session so page doesn't blank out
        const adminSession = getAdminSessionFromStorage();
        if (adminSession) setUser(adminSession.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper functions for admin session storage
  const getAdminSessionFromStorage = () => {
    if (typeof window !== 'undefined') {
      try {
        const user = JSON.parse(localStorage.getItem('admin_user') || 'null');
        const token = localStorage.getItem('admin_token');
        const timestamp = parseInt(localStorage.getItem('admin_timestamp') || '0');
        
        // Check if session is less than 24 hours old
        const isValid = user && (Date.now() - timestamp) < 24 * 60 * 60 * 1000;
        
        return isValid ? { user, token } : null;
      } catch (error) {
        console.error('Error reading admin session:', error);
        return null;
      }
    }
    return null;
  };
  
  const clearAdminSessionFromStorage = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_timestamp');
    }
  };

  // Initial authentication check on mount - always verify with server
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Always check with server first to get the true authentication state
      // This prevents stale admin sessions from persisting
      checkAuth();
    }
  }, []);

  useEffect(() => {
    // Only run on client-side and when user is authenticated
    if (typeof window === 'undefined' || !user) return;

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes (increased)
    const POLLING_INTERVAL = 30 * 60 * 1000; // 30 minutes (reduced frequency)
    
    let inactivityTimer;
    let lastActivity = Date.now();

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      lastActivity = Date.now();
      inactivityTimer = setTimeout(() => {
        // Double check if user is still authenticated before logging out
        if (user) {
          logout();
        }
      }, INACTIVITY_TIMEOUT);
    };

    const handleActivity = () => {
      // Throttle activity checks to prevent excessive timer resets
      const now = Date.now();
      if (now - lastActivity > 5000) { // Only reset if more than 5 seconds has passed
        resetInactivityTimer();
      }
    };

    // Set up event listeners with less frequency
    const throttledHandleActivity = throttle(handleActivity, 5000); // Throttle to once per 5 seconds
    
    window.addEventListener('mousemove', throttledHandleActivity, { passive: true });
    window.addEventListener('keydown', throttledHandleActivity, { passive: true });
    window.addEventListener('click', throttledHandleActivity, { passive: true });
    
    // Start inactivity timer
    resetInactivityTimer();

    return () => {
      clearTimeout(inactivityTimer);
      window.removeEventListener('mousemove', throttledHandleActivity);
      window.removeEventListener('keydown', throttledHandleActivity);
      window.removeEventListener('click', throttledHandleActivity);
    };
  }, [user?.id]); // Only depend on user ID to prevent frequent re-runs

  const login = async (email, password) => {
    try {
      if (!process.env.NEXT_PUBLIC_API_URL) {
        return { success: false, error: 'Configuration error: API URL not set' };
      }
      
      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      
      // Use unified login endpoint - backend determines user role
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'include', // CRITICAL: Include httpOnly cookies
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();

      if (response.ok && data.success) {
        // Clear any old admin sessions before setting new user
        clearAdminSessionFromStorage();

        setUser(data.user);
        setIsTimerActive(true); // Start inactivity timer

        // Store tokens in localStorage for admin panel
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }

        // Use backend's redirectTo response for navigation
        return {
          success: true,
          user: data.user,
          redirectTo: data.redirectTo
        };
      } else {
        if (response.status === 429) {
          return { success: false, error: 'Trop de tentatives. Veuillez patienter quelques minutes.' };
        } else if (response.status >= 500) {
          return { success: false, error: 'Erreur du serveur. Le service est temporairement indisponible.' };
        } else {
          return { success: false, error: data.message || 'Identifiants incorrects.' };
        }
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
      }
      return { success: false, error: 'Erreur de connexion. Veuillez réessayer.' };
    }
  };

  const register = async (userData) => {
    try {
      if (!process.env.NEXT_PUBLIC_API_URL) {
        return { success: false, error: 'Configuration error: API URL not set' };
      }
      
      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify(userData),
      });
      
      const data = await response.json();

      if (response.ok && data.success) {
        setUser(data.user);
        return { success: true, user: data.user };
      } else {
        if (response.status === 429) {
          return { success: false, error: 'Trop de tentatives. Veuillez patienter quelques minutes et réessayer.' };
        } else if (response.status >= 500) {
          return { success: false, error: 'Erreur du serveur. Le service est temporairement indisponible.' };
        } else if (response.status === 400) {
          return { success: false, error: data.message || 'Données invalides. Vérifiez vos informations.' };
        } else {
          return { success: false, error: data.message || 'Échec de l\'inscription.' };
        }
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
      }
      return { success: false, error: 'Erreur de connexion. Veuillez réessayer.' };
    }
  };

  const logout = async (redirectTo = '/') => {
    try {
      if (process.env.NEXT_PUBLIC_API_URL) {
        // Call backend logout to clear httpOnly cookies
        await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/logout`, {
          method: 'POST',
          credentials: 'include', // CRITICAL: Include httpOnly cookies
          headers: {
            'Content-Type': 'application/json',
          },
        });  
      }
    } catch (error) {
      // Ignore logout errors, continue with local cleanup
    } finally {
      setUser(null);
      setLoading(false);

      // Clean up any legacy token storage (if exists from old versions)
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');

      // Clear admin session backup
      clearAdminSessionFromStorage();

      // Clear only auth-related keys — never wipe game scores or leaderboard data
      if (typeof window !== 'undefined') {
        const AUTH_KEYS = ['token', 'refreshToken', 'user', 'authUser', 'accessToken', 'id_token'];
        AUTH_KEYS.forEach(k => localStorage.removeItem(k));
        sessionStorage.clear();
      }

      // Only redirect if not already on the target page
      if (typeof window !== 'undefined' && window.location.pathname !== redirectTo) {
        router.push(redirectTo);
      }
    }
  };

  const updateUser = async (updatedUserData) => {
    try {
      if (!process.env.NEXT_PUBLIC_API_URL) {
        return { success: false, error: 'Configuration error: API URL not set' };
      }

      const allowedUpdates = {};

      // Add fields only if they are provided
      if (updatedUserData.name !== undefined) allowedUpdates.name = updatedUserData.name;
      if (updatedUserData.firstName !== undefined) allowedUpdates.firstName = updatedUserData.firstName;
      if (updatedUserData.lastName !== undefined) allowedUpdates.lastName = updatedUserData.lastName;
      if (updatedUserData.phone !== undefined) allowedUpdates.phone = updatedUserData.phone;
      if (updatedUserData.address !== undefined) allowedUpdates.address = updatedUserData.address;
      if (updatedUserData.preferences !== undefined) allowedUpdates.preferences = updatedUserData.preferences;

      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${apiUrl}/users/profile`, {
        method: 'PUT',
        headers,
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify(allowedUpdates),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const updatedUser = data.user;
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      } else {
        return { success: false, error: data.message || 'Échec de la mise à jour du profil.' };
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
      }
      return { success: false, error: 'Erreur de connexion. Veuillez réessayer.' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      if (!process.env.NEXT_PUBLIC_API_URL) {
        return { success: false, error: 'Configuration error: API URL not set' };
      }

      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${apiUrl}/users/change-password`, {
        method: 'PUT',
        headers,
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      
      const data = await response.json();
      // Don't log password change response for security

      if (response.ok && data.success) {
        return { success: true, message: data.message };
      } else {
        
        return { success: false, error: data.message || 'Échec de la modification du mot de passe.' };
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
      }
      return { success: false, error: 'Erreur de connexion. Veuillez réessayer.' };
    }
  };

  // Check if user is authenticated (no token needed with httpOnly cookies)
  const isAuthenticated = !!user;

  // Check if user is admin - IMPORTANT: This checks both isAdmin and role
  const isAdmin = user?.isAdmin === true || user?.role === 'admin';

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  // Handle SSR/SSG where React context might not be available
  if (typeof window === 'undefined') {
    return {
      user: null,
      loading: true,
      isAuthenticated: false,
      isAdmin: false,
      login: () => Promise.resolve({ success: false, error: 'Not available during SSG' }),
      register: () => Promise.resolve({ success: false, error: 'Not available during SSG' }),
      logout: () => Promise.resolve(),
      updateUser: () => Promise.resolve({ success: false, error: 'Not available during SSG' }),
      changePassword: () => Promise.resolve({ success: false, error: 'Not available during SSG' }),
      checkAuth: () => Promise.resolve(),
    };
  }
  
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
